import {
  NICKNAME_MAX_LENGTH,
  type NicknameProblem,
  type NicknameStatus,
} from "@golazo/shared";
import type { OnlineClient } from "./client.js";

/**
 * Tu apodo: donde se guarda, quien lo puede cambiar y como se entera todo el
 * mundo.
 *
 * Los apodos son UNICOS en el servidor (2026-09-26). Eso cambia quien manda: el
 * que tienes escrito en este navegador es lo que PIDES, y el que vale es el que
 * te confirma el servidor. Si otro ya lo tiene, el servidor te deja el que
 * tenias (o un "Jugador" con cifras) y este modulo avisa para que elijas otro.
 *
 * Antes cada pantalla leia y escribia `golazo.nickname` por su cuenta, y el
 * cliente de red se conectaba SIEMPRE como "Jugador": el servidor solo sabia tu
 * nombre al entrar en una sala, y el chat del lobby y tus amigos te veian como
 * "Jugador".
 */
export const NICK_KEY = "golazo.nickname";
export const NICK_MAX = NICKNAME_MAX_LENGTH;

const listeners = new Set<(nick: string) => void>();

export function loadNickname(): string {
  try {
    return (localStorage.getItem(NICK_KEY) ?? "").trim().slice(0, NICK_MAX);
  } catch {
    return "";
  }
}

/** Guarda en local y avisa a quien lo pinta. No habla con el servidor. */
export function storeNickname(value: string): void {
  const clean = value.trim().replace(/\s+/g, " ").slice(0, NICK_MAX);
  if (clean === loadNickname()) return;
  try {
    localStorage.setItem(NICK_KEY, clean);
  } catch {
    // Sin almacenamiento: el nombre dura lo que la pestaña.
  }
  for (const fn of listeners) fn(clean);
}

export function onNicknameChange(fn: (nick: string) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/*
 * Quien sabe abrir el dialogo de cambiar apodo. Se registra una vez en
 * `main.ts`, y asi cualquier pantalla (el lobby, personalizar, el menu) puede
 * ofrecer "cambiar" sin que haya que pasarle el dialogo por constructor.
 */
let editor: ((notice?: string) => Promise<string | null>) | null = null;

export function registerNicknameEditor(fn: (notice?: string) => Promise<string | null>): () => void {
  editor = fn;
  return () => {
    if (editor === fn) editor = null;
  };
}

export function editNickname(notice?: string): Promise<string | null> {
  return editor ? editor(notice) : Promise.resolve(null);
}

export type NicknameSetResult =
  | { ok: true; nickname: string; offline?: boolean }
  | { ok: false; reason: NicknameProblem | "timeout"; nickname: string };

export interface NicknameService {
  current(): string;
  /**
   * Pide el apodo. Sin conexion se guarda en local y se reclama al conectar:
   * si para entonces es de otro, llega el aviso de `onTaken`.
   */
  set(nickname: string): Promise<NicknameSetResult>;
  /** ¿Esta libre? `unknown` = sin conexion para preguntar. */
  check(nickname: string): Promise<NicknameStatus | "unknown">;
  /** El servidor no pudo darte el nombre que tenias guardado. */
  onTaken(fn: (requested: string) => void): () => void;
  dispose(): void;
}

const SET_TIMEOUT_MS = 5000;
const CHECK_TIMEOUT_MS = 3000;

export function createNicknameService(client: OnlineClient): NicknameService {
  const takenListeners = new Set<(requested: string) => void>();
  /** Las respuestas a `nickname:set` llegan en orden: una cola basta. */
  const pendingSets: Array<(r: NicknameSetResult) => void> = [];
  const pendingChecks = new Map<string, Array<(s: NicknameStatus | "unknown") => void>>();

  const unsub = client.onMessage((msg) => {
    if (msg.type === "welcome" || msg.type === "account:redeemed") {
      // El que vale es el del servidor: puede haberlo corregido (filtro) o
      // habertelo negado (cogido). Sin esto el lobby pintaria uno y los demas
      // verian otro.
      if (msg.nickname) storeNickname(msg.nickname);
      if (msg.type === "welcome" && msg.nicknameTaken) {
        for (const fn of takenListeners) fn(msg.nicknameTaken);
      }
      return;
    }
    if (msg.type === "nickname:result") {
      const resolve = pendingSets.shift();
      if (msg.ok) storeNickname(msg.nickname);
      resolve?.(
        msg.ok
          ? { ok: true, nickname: msg.nickname }
          : { ok: false, reason: msg.reason ?? "invalid", nickname: msg.nickname },
      );
      return;
    }
    if (msg.type === "nickname:check") {
      const waiting = pendingChecks.get(msg.nickname);
      if (!waiting) return;
      pendingChecks.delete(msg.nickname);
      for (const resolve of waiting) resolve(msg.status);
    }
  });

  return {
    current: loadNickname,

    set(nickname) {
      const clean = nickname.trim().replace(/\s+/g, " ").slice(0, NICK_MAX);
      if (client.state !== "online") {
        storeNickname(clean);
        return Promise.resolve({ ok: true, nickname: clean, offline: true });
      }
      return new Promise<NicknameSetResult>((resolve) => {
        let settled = false;
        const done = (r: NicknameSetResult): void => {
          if (settled) return;
          settled = true;
          resolve(r);
        };
        pendingSets.push(done);
        client.send({ type: "nickname:set", nickname: clean });
        window.setTimeout(() => {
          const at = pendingSets.indexOf(done);
          if (at >= 0) pendingSets.splice(at, 1);
          done({ ok: false, reason: "timeout", nickname: loadNickname() });
        }, SET_TIMEOUT_MS);
      });
    },

    check(nickname) {
      const clean = nickname.trim().replace(/\s+/g, " ").slice(0, NICK_MAX);
      if (client.state !== "online" || !clean) return Promise.resolve("unknown");
      return new Promise((resolve) => {
        const list = pendingChecks.get(clean) ?? [];
        list.push(resolve);
        pendingChecks.set(clean, list);
        client.send({ type: "nickname:check", nickname: clean });
        window.setTimeout(() => {
          const waiting = pendingChecks.get(clean);
          if (!waiting?.includes(resolve)) return;
          waiting.splice(waiting.indexOf(resolve), 1);
          if (waiting.length === 0) pendingChecks.delete(clean);
          resolve("unknown");
        }, CHECK_TIMEOUT_MS);
      });
    },

    onTaken(fn) {
      takenListeners.add(fn);
      return () => takenListeners.delete(fn);
    },

    dispose() {
      unsub();
      takenListeners.clear();
    },
  };
}
